import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../config";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogContent,
  Snackbar,
  CircularProgress,
  Fade,
  Container,
  Chip,
  Paper,
  Stack,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import logo from "../assets/mainlogo.png";
import { toast } from "react-toastify";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;
const orbFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-20px)}`;
const ripple = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,.25)}50%{box-shadow:0 0 0 10px rgba(59,130,246,0)}`;
const scanL = keyframes`0%{transform:translateY(-100%)}100%{transform:translateY(3000%)}`;

const PALETTES = [
  {
    from: "#1d4ed8",
    to: "#60a5fa",
    shadow: "rgba(29,78,216,.3)",
    light: "#dbeafe",
    text: "#1e40af",
    icon: "📐",
  },
  {
    from: "#6d28d9",
    to: "#a78bfa",
    shadow: "rgba(109,40,217,.28)",
    light: "#ede9fe",
    text: "#5b21b6",
    icon: "🔬",
  },
  {
    from: "#0369a1",
    to: "#38bdf8",
    shadow: "rgba(3,105,161,.28)",
    light: "#e0f2fe",
    text: "#075985",
    icon: "📚",
  },
  {
    from: "#065f46",
    to: "#34d399",
    shadow: "rgba(6,95,70,.28)",
    light: "#d1fae5",
    text: "#064e3b",
    icon: "🌍",
  },
  {
    from: "#9f1239",
    to: "#fb7185",
    shadow: "rgba(159,18,57,.28)",
    light: "#ffe4e6",
    text: "#881337",
    icon: "💻",
  },
  {
    from: "#92400e",
    to: "#fbbf24",
    shadow: "rgba(146,64,14,.28)",
    light: "#fef3c7",
    text: "#78350f",
    icon: "⚗️",
  },
];

// ── styled ──
const Root = styled(Box)({
  minHeight: "100vh",
  height: "100vh",
  background: "#f0f3f9",
  fontFamily: "'Sora',sans-serif",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

const TopBar = styled(Box)({
  background: "#fff",
  borderBottom: "1px solid #e8edf5",
  position: "sticky",
  top: 0,
  zIndex: 100,
  boxShadow: "0 1px 0 #e8edf5, 0 4px 24px -4px rgba(15,23,42,.04)",
  flexShrink: 0,
});

const Hero = styled(Box)({
  background: "#07101f",
  position: "relative",
  overflow: "hidden",
  padding: "60px 0 96px",
  flexShrink: 0,
});

const HeroGrid = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(99,179,237,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,179,237,.04) 1px,transparent 1px)",
  backgroundSize: "52px 52px",
  pointerEvents: "none",
});

const HeroScan = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  height: 2,
  background:
    "linear-gradient(90deg,transparent,rgba(96,165,250,.13),transparent)",
  animation: `${scanL} 8s linear infinite`,
  pointerEvents: "none",
});

const HeroOrb = styled(Box)(({ l, t, size, color, delay }) => ({
  position: "absolute",
  left: l,
  top: t,
  width: size,
  height: size,
  borderRadius: "50%",
  background: color,
  filter: "blur(88px)",
  opacity: 0.5,
  animation: `${orbFloat} ${8 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
}));

const WaveCut = styled(Box)({
  position: "absolute",
  bottom: -2,
  left: 0,
  right: 0,
  height: 64,
  background: "#f0f3f9",
  clipPath: "ellipse(58% 100% at 50% 100%)",
});

const AvatarRing = styled(Avatar)({
  width: 84,
  height: 84,
  border: "3px solid rgba(255,255,255,.25)",
  boxShadow: "0 12px 36px rgba(0,0,0,.25)",
  // background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
  fontSize: "1.9rem",
});

const SubCard = styled(Card)(({ pal, selected }) => ({
  borderRadius: 20,
  border: selected ? `2px solid ${pal.from}` : "1.5px solid #e2e8f0",
  background: "#fff",
  boxShadow: selected
    ? `0 0 0 4px ${pal.shadow}`
    : "0 2px 12px -4px rgba(15,23,42,.07)",
  transition: "all .32s cubic-bezier(.22,1,.36,1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-9px)",
    boxShadow: `0 22px 52px -12px ${pal.shadow}`,
    borderColor: "transparent",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg,${pal.from},${pal.to})`,
    opacity: selected ? 1 : 0,
    transition: "opacity .32s ease",
  },
  "&:hover::after": { opacity: 1 },
}));

const BeginBtn = styled(Button)(({ pal }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".82rem",
  padding: "9px 16px",
  background: `linear-gradient(105deg,${pal.from},${pal.to})`,
  backgroundSize: "200% auto",
  color: "#fff",
  boxShadow: `0 6px 18px -6px ${pal.shadow}`,
  transition: "all .28s ease",
  "&:hover": {
    animation: `${shimmer} 1s linear infinite`,
    transform: "translateY(-2px)",
    boxShadow: `0 12px 24px -6px ${pal.shadow}`,
  },
}));

const LogoutBtn = styled(Button)({
  borderRadius: 10,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 600,
  fontSize: ".83rem",
  color: "#64748b",
  border: "1.5px solid #e2e8f0",
  padding: "7px 16px",
  transition: "all .22s ease",
  "&:hover": {
    background: "#fff1f2",
    borderColor: "#fca5a5",
    color: "#dc2626",
  },
});

const ContentWrapper = styled(Box)({
  flex: 1, // Take remaining height
  overflow: "auto", // Make this scrollable
  paddingBottom: "40px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#94a3b8",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#64748b",
  },
});

const ScrollableContent = styled(Container)({
  paddingTop: "24px",
  paddingBottom: "40px",
});

const InsRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "11px 0",
  borderBottom: "1px solid #f1f5f9",
  "&:last-child": { borderBottom: "none" },
});

// ── component ──
export default function SubjectsScreen() {
  const navigate = useNavigate();
  const [studentName, setStudentName] = useState("Student");
  const [profilePic, setProfilePic] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [loading, setLoading] = useState(true);
  const [toasty, setToasty] = useState({ open: false, message: "" });
  const studCode = localStorage.getItem("stud_code");

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const API_URL = getApiUrl();
    const token = JSON.parse(localStorage.getItem("authToken"));
    if (!token || !studCode) {
      navigate("/student-login");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/student-subjects/${studCode}/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Failed to load subjects.");
        setStudentName(d.student_name || "Student");
        setProfilePic(d.profile_pic || "");
        setSubjects(d.subjects || []);
        if (d.profile_pic) {
          document.body.style.backgroundImage = `url('${d.profile_pic}')`;
        }
      } catch (e) {
        setToasty({ open: true, message: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [studCode, navigate]);

  const handleLogout = () => {
    const id = toast.info(
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Are you sure you want to logout?
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              localStorage.clear();
              navigate("/student-login");
              toast.dismiss(id);
            }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => toast.dismiss(id)}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>,
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  };

  const startExam = () => {
    if (selectedId) navigate(`/student-exam?subjectId=${selectedId}`);
  };

  const firstName = studentName.split(" ")[0];
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const instructions = [
    "Ensure you have a stable internet connection before starting.",
    "Do not close the browser or turn off your device during the exam.",
    "The exam will auto-submit when the timer reaches zero.",
    "Manage your time wisely — each section has a set allocation.",
    "You may use a rough sheet for working calculations.",
  ];

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.8,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "10px",
                  // background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    filter: "drop-shadow(0 4px 16px rgba(147,197,253,0.4))",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  color: "#0f172a",
                  fontSize: "1.05rem",
                }}
              >
                LiteUpper
              </Typography>
            </Box>
            <LogoutBtn
              onClick={handleLogout}
              startIcon={<LogoutIcon sx={{ fontSize: "1rem !important" }} />}
            >
              Sign Out
            </LogoutBtn>
          </Box>
        </Container>
      </TopBar>

      {/* HERO */}
      <Hero>
        <HeroGrid />
        <HeroScan />
        <HeroOrb
          l="-120px"
          t="-100px"
          size="400px"
          color="radial-gradient(circle,rgba(37,99,235,.5),transparent)"
          delay={0}
        />
        <HeroOrb
          l="65%"
          t="30%"
          size="260px"
          color="radial-gradient(circle,rgba(16,185,129,.25),transparent)"
          delay={2}
        />
        <HeroOrb
          l="30%"
          t="60%"
          size="180px"
          color="radial-gradient(circle,rgba(124,58,237,.2),transparent)"
          delay={3.5}
        />
        <WaveCut />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={700}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box sx={{ position: "relative", background: "transparent " }}>
                <AvatarRing
                  src={profilePic}
                  sx={{ background: "transparent " }}
                >
                  {!profilePic && firstName[0]}
                </AvatarRing>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 2,
                    right: 2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#4ade80",
                    border: "2px solid #07101f",
                    boxShadow: "0 0 8px #4ade80",
                  }}
                />
              </Box>
              <Box>
                <Chip
                  label={`${subjects.length} subject${
                    subjects.length !== 1 ? "s" : ""
                  } available`}
                  size="small"
                  sx={{
                    background: "rgba(255,255,255,.1)",
                    color: "rgba(255,255,255,.85)",
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".72rem",
                    fontWeight: 600,
                    border: "1px solid rgba(255,255,255,.15)",
                    mb: 1,
                    backdropFilter: "blur(8px)",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'DM Serif Display',serif",
                    fontSize: { xs: "1.8rem", md: "2.3rem" },
                    color: "#fff",
                    lineHeight: 1.14,
                    mb: 0.5,
                  }}
                >
                  {greeting},{" "}
                  <Box
                    component="span"
                    sx={{ color: "#93c5fd", fontStyle: "italic" }}
                  >
                    {firstName}
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "rgba(255,255,255,.5)",
                    fontSize: ".88rem",
                  }}
                >
                  Select a subject below to begin your examination
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Hero>

      {/* CONTENT */}
      <ContentWrapper>
        <ScrollableContent maxWidth="lg">
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <Container maxWidth="lg" sx={{ py: 6 }}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontFamily: "'DM Serif Display',serif",
                    fontSize: "1.55rem",
                    color: "#0f172a",
                    mb: 0.4,
                  }}
                >
                  Your Subjects
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "#94a3b8",
                    fontSize: ".85rem",
                  }}
                >
                  Click a subject to review instructions, then start your exam
                </Typography>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 14,
                    gap: 2,
                  }}
                >
                  <CircularProgress
                    size={46}
                    thickness={4}
                    sx={{ color: "#3b82f6" }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      color: "#94a3b8",
                      fontSize: ".87rem",
                    }}
                  >
                    Loading your subjects…
                  </Typography>
                </Box>
              ) : subjects.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2,1fr)",
                      md: "repeat(3,1fr)",
                    },
                    gap: 3,
                  }}
                >
                  {subjects.map((subject, i) => {
                    const pal = PALETTES[i % PALETTES.length];
                    const isSelected = selectedId === subject.id;
                    return (
                      <Fade in timeout={300 + i * 80} key={subject.id}>
                        <Box
                          sx={{
                            animation: `${fadeUp} ${
                              0.4 + i * 0.07
                            }s cubic-bezier(.22,1,.36,1) both`,
                            animationDelay: `${i * 0.06}s`,
                          }}
                        >
                          <SubCard
                            pal={pal}
                            selected={isSelected}
                            elevation={0}
                            onClick={() => {
                              setSelectedId(subject.id);
                              setSelectedName(subject.name);
                            }}
                          >
                            <CardContent sx={{ p: "26px 26px 22px" }}>
                              {/* Palette icon */}
                              <Box
                                sx={{
                                  width: 58,
                                  height: 58,
                                  borderRadius: "17px",
                                  background: `linear-gradient(135deg,${pal.from},${pal.to})`,
                                  boxShadow: `0 10px 24px -6px ${pal.shadow}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "1.5rem",
                                  mb: 2.5,
                                }}
                              >
                                {pal.icon}
                              </Box>

                              <Typography
                                sx={{
                                  fontFamily: "'Sora',sans-serif",
                                  fontWeight: 700,
                                  fontSize: "1rem",
                                  color: "#0f172a",
                                  mb: 0.5,
                                }}
                              >
                                {subject.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "'Sora',sans-serif",
                                  color: "#94a3b8",
                                  fontSize: ".8rem",
                                  mb: 3,
                                }}
                              >
                                Tap to review instructions & begin
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    background: pal.light,
                                    color: pal.text,
                                    fontFamily: "'Sora',sans-serif",
                                    fontSize: ".7rem",
                                    fontWeight: 700,
                                    px: 1.4,
                                    py: 0.5,
                                    borderRadius: 100,
                                    letterSpacing: ".05em",
                                  }}
                                >
                                  EXAM
                                </Box>
                                <BeginBtn
                                  pal={pal}
                                  variant="contained"
                                  endIcon={
                                    <PlayArrowIcon
                                      sx={{ fontSize: ".95rem !important" }}
                                    />
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedId(subject.id);
                                    setSelectedName(subject.name);
                                  }}
                                >
                                  Begin
                                </BeginBtn>
                              </Box>
                            </CardContent>

                            {isSelected && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 14,
                                  right: 14,
                                  width: 26,
                                  height: 26,
                                  borderRadius: "50%",
                                  background: pal.from,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: `0 4px 12px ${pal.shadow}`,
                                  animation: `${ripple} 2s ease-in-out infinite`,
                                }}
                              >
                                <CheckCircleOutlineIcon
                                  sx={{ color: "#fff", fontSize: 15 }}
                                />
                              </Box>
                            )}
                          </SubCard>
                        </Box>
                      </Fade>
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 13,
                    background: "#fff",
                    borderRadius: "22px",
                    border: "1.5px dashed #dbeafe",
                  }}
                >
                  <Typography sx={{ fontSize: "3rem", mb: 2 }}>📭</Typography>
                  <Typography
                    sx={{
                      fontFamily: "'DM Serif Display',serif",
                      fontSize: "1.4rem",
                      color: "#0f172a",
                      mb: 1,
                    }}
                  >
                    No subjects yet
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      color: "#94a3b8",
                      fontSize: ".87rem",
                    }}
                  >
                    No active subjects available at this time.
                  </Typography>
                </Box>
              )}
            </Container>
          </Box>
        </ScrollableContent>
      </ContentWrapper>

      {/* INSTRUCTIONS DIALOG */}
      <Dialog
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: "22px",
            boxShadow: "0 48px 96px -24px rgba(15,23,42,.22)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "#07101f",
            p: "30px 34px 26px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <HeroGrid />
          <HeroOrb
            l="-60px"
            t="-60px"
            size="180px"
            color="radial-gradient(circle,rgba(37,99,235,.4),transparent)"
          />
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: ".72rem",
              fontWeight: 700,
              color: "rgba(255,255,255,.45)",
              letterSpacing: ".09em",
              textTransform: "uppercase",
              mb: 0.7,
              position: "relative",
              zIndex: 1,
            }}
          >
            Before you begin
          </Typography>
          <Typography
            sx={{
              fontFamily: "'DM Serif Display',serif",
              fontSize: "1.65rem",
              color: "#fff",
              lineHeight: 1.2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedName}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              color: "rgba(255,255,255,.45)",
              fontSize: ".84rem",
              mt: 0.5,
              position: "relative",
              zIndex: 1,
            }}
          >
            Exam Instructions
          </Typography>
        </Box>

        <DialogContent sx={{ p: "26px 34px 6px" }}>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              fontSize: ".7rem",
              color: "#475569",
              letterSpacing: ".08em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Please read carefully
          </Typography>
          {instructions.map((ins, i) => (
            <InsRow key={i}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "8px",
                  background: "#dbeafe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: ".72rem",
                    color: "#2563eb",
                  }}
                >
                  {i + 1}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".88rem",
                  color: "#334155",
                  lineHeight: 1.6,
                }}
              >
                {ins}
              </Typography>
            </InsRow>
          ))}
          <Box
            sx={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "11px",
              p: "11px 15px",
              mt: 2,
              mb: 1,
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <Typography sx={{ fontSize: ".95rem" }}>⚠️</Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: ".82rem",
                color: "#92400e",
                lineHeight: 1.6,
              }}
            >
              Once started, the exam timer <b>cannot be paused</b>. Ensure
              you're fully ready before proceeding.
            </Typography>
          </Box>
        </DialogContent>

        <Box sx={{ p: "18px 34px 30px", display: "flex", gap: 2 }}>
          <Button
            onClick={() => setSelectedId(null)}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: "11px",
              textTransform: "none",
              fontFamily: "'Sora',sans-serif",
              fontWeight: 600,
              py: 1.4,
              fontSize: ".88rem",
              color: "#64748b",
              borderColor: "#e2e8f0",
              borderWidth: 1.5,
              "&:hover": { background: "#f8faff", borderColor: "#cbd5e1" },
            }}
          >
            Go Back
          </Button>
          <Button
            onClick={startExam}
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: "11px",
              textTransform: "none",
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              py: 1.4,
              fontSize: ".88rem",
              background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
              boxShadow: "0 10px 24px -6px rgba(37,99,235,.38)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 14px 30px -6px rgba(37,99,235,.48)",
              },
              transition: "all .28s ease",
            }}
          >
            Start Exam
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={toasty.open}
        autoHideDuration={4000}
        onClose={() => setToasty({ open: false, message: "" })}
        message={toasty.message}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            fontFamily: "'Sora',sans-serif",
            borderRadius: "11px",
            background: "#0f172a",
          },
        }}
      />
    </Root>
  );
}
